import { type FC, useCallback } from "react";

import { serverEndSession } from "api/api-server-actions";
import { useVirtualClassActions } from "components/av/virtual-class-utils";
import { ClassSessionCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { Menu, MenuDivider, MenuItem, SubMenu } from "mds/components/Menu";
import { PillButton } from "mds/components/PillButton";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import {
  ChatWithLinesIcon,
  ChevronDownIcon,
  CopySendArrowIcon,
  HandRaiseOutlineIcon,
  InPersonIcon,
  PersonGroupIcon,
  ReactionIcon,
  RepeatIcon,
  VideoIcon,
} from "mds/icons";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppSelector } from "store/index";
import {
  selectActiveClassSessionCategory,
  selectActiveClassSessionId,
  selectCurrentClassUserId,
  selectCurrentClassUserIsInPersonClass,
  selectCurrentCourseAvEnabled,
  selectCurrentCourseId,
  selectIsPracticeClassSession,
} from "store/selectors";
import { useAv } from "utils/av";
import { useClassActions } from "utils/class-utils";
import { useJoinAsCourseAdmin } from "utils/hooks/use-join-as-course-admin";

export const SessionButton: FC = () => {
  const isSmallScreen = !useIsMdOrLarger();
  const { canAuthorCourse } = useCapabilities();
  const classSessionId = useAppSelector(selectActiveClassSessionId);
  const classSessionCategory = useAppSelector(selectActiveClassSessionCategory);
  const courseId = useAppSelector(selectCurrentCourseId);
  const currentClassUserId = useAppSelector(selectCurrentClassUserId);
  const isInInPersonClass = useAppSelector(selectCurrentClassUserIsInPersonClass);
  const isPracticeClassSession = useAppSelector(selectIsPracticeClassSession);
  const avEnabled = useAppSelector(selectCurrentCourseAvEnabled);

  const { isInVirtualClass, isAvActiveInAnotherTab } = useAv();
  const { joinClass, leaveClass } = useClassActions();
  const { showAvCheckToJoinVirtualClass } = useVirtualClassActions();
  const { joinAsCourseAdminIfNew } = useJoinAsCourseAdmin();

  const startInPersonSession = async () => {
    const { classUserId } = await joinAsCourseAdminIfNew();

    await storeApi.class_sessions.create({
      course_id: courseId!,
      category: ClassSessionCategory.IN_PERSON,
    });

    joinClass(classUserId);
  };

  const endInPersonSession = () => {
    if (!classSessionId) return;
    serverEndSession(classSessionId);
  };

  const startPracticeInPersonSession = async () => {
    await storeApi.class_sessions.create({
      course_id: courseId,
      category: ClassSessionCategory.IN_PERSON,
      is_practice: true,
    });

    joinClass();
  };

  const onJoinSession = useCallback(() => {
    if (classSessionCategory === ClassSessionCategory.IN_PERSON) {
      joinClass();
    } else {
      showAvCheckToJoinVirtualClass();
    }
  }, [classSessionCategory, joinClass, showAvCheckToJoinVirtualClass]);

  if (
    !courseId ||
    isInVirtualClass ||
    isAvActiveInAnotherTab ||
    (!canAuthorCourse && !classSessionCategory) ||
    (!canAuthorCourse && !currentClassUserId) ||
    (classSessionCategory === ClassSessionCategory.VIRTUAL && isSmallScreen)
  ) {
    return null;
  }

  if (canAuthorCourse && classSessionCategory === ClassSessionCategory.IN_PERSON) {
    return (
      <PillButton
        className="gap-2"
        eventName="End Current Session"
        kind="red"
        size="m"
        onClick={endInPersonSession}
      >
        <CopySendArrowIcon />
        {isPracticeClassSession
          ? t("course.start_session_menu.end_practice")
          : t("course.start_session_menu.end_session")}
      </PillButton>
    );
  }

  if (!canAuthorCourse && isInInPersonClass) {
    return (
      <PillButton
        className="gap-2"
        eventName="Leave Session"
        kind="red"
        size="m"
        onClick={leaveClass}
      >
        <CopySendArrowIcon />
        {t("course.start_session_menu.leave_session")}
      </PillButton>
    );
  }

  const MenuButton = (
    <PillButton className="gap-2" eventName="Start a Session - Open Menu" kind="dark-gray" size="m">
      {t("course.start_session_menu.title")}
      <ChevronDownIcon />
    </PillButton>
  );

  const iconCircleClasses = "flex size-6 items-center justify-center rounded-full bg-black-tint-97";

  if (canAuthorCourse) {
    return (
      <Menu align="end" direction="bottom" limitItemHeight={false} menuButton={MenuButton}>
        <MenuItem
          aria-label={t("course.start_session_menu.in_person")}
          eventName="Start a Session - In-Person"
          onClick={startInPersonSession}
        >
          <div className="p-1">
            <div className="flex justify-between">
              <IconText
                iconStart={<InPersonIcon className="text-blue" />}
                text={t("course.start_session_menu.in_person")}
              />
              <div className="flex items-center gap-1">
                <div className={iconCircleClasses}>
                  <ReactionIcon />
                </div>

                <div className={iconCircleClasses}>
                  <ChatWithLinesIcon />
                </div>

                <div className={iconCircleClasses}>
                  <HandRaiseOutlineIcon />
                </div>
              </div>
            </div>

            <div className="mt-1.5 max-w-[290px] text-xs text-black-tint-40">
              {t("course.start_session_menu.in_person_description")}
            </div>
          </div>
        </MenuItem>

        {avEnabled && !isSmallScreen && (
          <>
            <MenuDivider />
            <MenuItem
              aria-label={t("course.start_session_menu.virtual_class")}
              eventName="Start a Session - Virtual"
              onClick={() => showAvCheckToJoinVirtualClass()}
            >
              <div className="p-1">
                <div className="flex justify-between">
                  <IconText
                    iconStart={<VideoIcon className="text-blue" />}
                    text={t("course.start_session_menu.virtual")}
                  />

                  <div className="flex items-center gap-1">
                    <div className={iconCircleClasses}>
                      <VideoIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <PersonGroupIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <ReactionIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <ChatWithLinesIcon />
                    </div>

                    <div className={iconCircleClasses}>
                      <HandRaiseOutlineIcon />
                    </div>
                  </div>
                </div>

                <div className="mt-1.5 max-w-[290px] text-xs text-black-tint-40">
                  {t("course.start_session_menu.virtual_description")}
                </div>
              </div>
            </MenuItem>
          </>
        )}
        <MenuDivider />
        <SubMenu
          label={
            <IconText
              iconStart={<RepeatIcon className="text-blue" />}
              text={t("course.start_session_menu.practice")}
            />
          }
          withArrow
        >
          <MenuItem eventName="Practice - In-Person" onClick={startPracticeInPersonSession}>
            <IconText
              iconStart={<InPersonIcon />}
              text={t("course.start_session_menu.in_person")}
            />
          </MenuItem>
          {avEnabled && !isSmallScreen && (
            <>
              <MenuDivider />
              <MenuItem
                eventName="Practice - Virtual"
                onClick={() => showAvCheckToJoinVirtualClass(true)}
              >
                <IconText iconStart={<VideoIcon />} text={t("course.start_session_menu.virtual")} />
              </MenuItem>
            </>
          )}
        </SubMenu>
      </Menu>
    );
  }

  return (
    <PillButton
      className="gap-2"
      eventName="Join Session"
      kind="blue"
      size="m"
      onClick={onJoinSession}
    >
      {classSessionCategory === ClassSessionCategory.IN_PERSON ? <InPersonIcon /> : <VideoIcon />}
      {t("footer.join_session")}
    </PillButton>
  );
};
